<template>
  <el-popover
    v-model:visible="open"
    placement="bottom-end"
    trigger="click"
    :width="320"
    popper-class="rb-compare-popover"
  >
    <template #reference>
      <el-button
        :type="enabled ? 'primary' : 'default'"
        plain
        class="rb-compare-btn"
        :class="{ 'rb-compare-btn--sm': compact }"
        :title="$t('ReportBuilder.compare.title')"
      >
        <i class="fas fa-code-compare rb-compare-icon"></i>
        <span class="rb-compare-text">{{ buttonText }}</span>
      </el-button>
    </template>

    <div class="cmp-body">
      <div class="cmp-head">
        <span class="cmp-title">{{ $t('ReportBuilder.compare.title') }}</span>
        <el-switch :model-value="enabled" :disabled="!supported" @change="toggle" />
      </div>

      <p v-if="!supported" class="cmp-hint">{{ $t('ReportBuilder.compare.unsupported') }}</p>

      <template v-else-if="enabled">
        <el-radio-group :model-value="mode" class="cmp-modes" @change="setMode">
          <el-radio value="previous" class="cmp-mode">{{ $t('ReportBuilder.compare.previous') }}</el-radio>
          <el-radio value="previous_year" class="cmp-mode">{{ $t('ReportBuilder.compare.previousYear') }}</el-radio>
          <el-radio value="custom" class="cmp-mode">{{ $t('ReportBuilder.compare.custom') }}</el-radio>
        </el-radio-group>

        <!-- teleported=false обязателен: по умолчанию панель календаря уезжает в body,
             и любой клик в ней (выбор даты, стрелки месяцев) поповер считает внешним
             и закрывается вместе с самой панелью. Внутри поповера клики «свои». -->
        <el-date-picker
          v-if="mode === 'custom'"
          v-model="customRange"
          type="daterange"
          unlink-panels
          :teleported="false"
          popper-class="cmp-picker-popper"
          range-separator="—"
          :start-placeholder="$t('ReportBuilder.dateRange.startDate')"
          :end-placeholder="$t('ReportBuilder.dateRange.endDate')"
          value-format="YYYY-MM-DD HH:mm:ss"
          :default-time="[new Date(2000, 1, 1, 0, 0, 0), new Date(2000, 1, 1, 23, 59, 59)]"
          class="cmp-picker"
          @change="onCustomRangeChange"
        />

        <div v-if="periodText" class="cmp-period">
          <i class="fas fa-calendar-alt cmp-period-icon"></i>
          <span>{{ periodText }}</span>
        </div>
        <p class="cmp-hint">{{ forceSupported ? $t('ReportBuilder.compare.dashboardHint') : $t('ReportBuilder.compare.hint') }}</p>
      </template>
    </div>
  </el-popover>
</template>

<script>
// Переключатель сравнения периодов. Пишет конфиг в visual.compare — поле, которое
// бэкенд не читает, поэтому настройка сохраняется вместе с отчётом и виджетом дашборда.
// Сам второй запрос делает store (loadCompareData), см. helpers/reports/compare.js.
import { normalizeCompare, resolveComparePeriod, resolveDateToken, isCompareSupported } from '@/helpers/reports/compare'
import { setDashboardCompareOverride } from '@/helpers/dashboardCompareOverride'

export default {
  name: 'CompareControl',
  props: {
    compact: {
      type: Boolean,
      default: false
    },
    // Дашборд: в глобальном сторе лежит не отчёт, а лишь общий период, поэтому
    // проверять по нему тип отчёта бессмысленно. Поддержку решает каждый виджет
    // сам (isCompareSupported внутри Report.loadCompareData).
    forceSupported: {
      type: Boolean,
      default: false
    },
    // Передаётся только со страницы дашборда: включён/выключен ли переключатель
    // на конкретном дашборде — личный выбор пользователя, сохраняем в localStorage,
    // чтобы default_compare дашборда не перезатирал его при следующем открытии.
    dashboardId: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      open: false,
      customRange: []
    }
  },
  computed: {
    state() {
      return this.$store.state.ReportFromBuilder
    },
    compare() {
      return normalizeCompare(this.state.visual)
    },
    enabled() {
      return !!this.compare
    },
    supported() {
      return this.forceSupported || isCompareSupported(this.state.type, this.state.type_chart)
    },
    mode() {
      return this.compare?.mode || 'previous'
    },
    granularity() {
      return this.state.source_request?.granularity || 'day'
    },
    // Период, которым отчёт РЕАЛЬНО был запрошен. Оценка на клиенте — только пока
    // ответа нет: относительные токены (`-30d`) резолвит бэкенд в своём часовом поясе,
    // и у клиента в другом поясе оценка может разойтись на сутки.
    resolvedPeriod() {
      if (!this.compare) return null
      const actual = this.state.comparePeriod
      if (actual?.from && actual?.to) {
        return { fromDate: resolveDateToken(actual.from), toDate: resolveDateToken(actual.to) }
      }
      const params = this.state.settings?.params || {}
      return resolveComparePeriod(params[':date_from'], params[':date_to'], this.compare, this.granularity)
    },
    periodText() {
      const p = this.resolvedPeriod
      if (!p?.fromDate || !p?.toDate) return ''
      return this.formatDate(p.fromDate) + ' — ' + this.formatDate(p.toDate)
    },
    // Подпись не меняется от режима: он и период видны в попапе, а на кнопке важно
    // только «включено / выключено» — это показывает её тип (primary vs default)
    buttonText() {
      return this.$t('ReportBuilder.compare.button')
    }
  },
  methods: {
    formatDate(date) {
      if (!date) return ''
      const months = this.$tm('UserReports.view.months')
      return date.getDate() + ' ' + months[date.getMonth()] + ' ' + date.getFullYear()
    },
    commit(patch) {
      this.$store.commit('ReportFromBuilder/setCompare', patch)
    },
    toggle(val) {
      if (val && !this.state.visual?.compare?.mode) {
        this.commit({ enabled: true, mode: 'previous' })
      } else {
        this.commit({ enabled: !!val })
      }
      if (this.dashboardId) {
        setDashboardCompareOverride(this.dashboardId, !!val)
      }
    },
    setMode(val) {
      this.commit({ mode: val })
      if (val === 'custom' && this.customRange?.length === 2) {
        this.commit({ date_from: this.customRange[0], date_to: this.customRange[1] })
      }
    },
    onCustomRangeChange(val) {
      if (!val || val.length !== 2) return
      this.commit({ date_from: val[0], date_to: val[1] })
    }
  },
  // Сознательно НЕ сбрасываем visual.compare при переключении на тип отчёта без
  // поддержки сравнения (воронки, ретеншен, пути, SQL): там оно полностью инертно —
  // isCompareSupported глушит и запрос, и отрисовку, — а при возврате к трендам
  // настройка пользователя сохраняется. Сбрасывать здесь всё равно было бы нельзя:
  // для этих типов _chart.vue не рендерится и компонент размонтирован.
  mounted() {
    const c = this.compare
    if (c?.mode === 'custom' && c.date_from && c.date_to) {
      this.customRange = [c.date_from, c.date_to]
    }
  }
}
</script>

<style scoped>
.rb-compare-btn {
  border-radius: 10px;
  font-weight: 500;
  white-space: nowrap;
}

/* compact — для панели страницы отчёта и тулбара дашборда, где соседи (кнопка периода,
   чипы фильтров) имеют высоту 28px. В конструкторе кнопка стоит рядом с el-select на
   32px, поэтому там остаётся стандартный размер. */
.rb-compare-btn--sm {
  height: 28px;
  /* глобальный стиль задаёт .el-button { min-height: 36px } — без сброса он победит height */
  min-height: 28px;
  padding: 0 10px;
  border-radius: 7px;
  font-size: 12px;
  font-weight: 600;
}

.rb-compare-btn--sm i {
  font-size: 11px;
}

.rb-compare-icon {
  margin-right: 4px;
}

/* На мобилке compact-кнопка (панель дашборда/отчёта) занимает меньше места —
   текст убираем, оставляем только иконку; название всё ещё доступно из :title */
@media (max-width: 767.98px) {
  .rb-compare-btn--sm .rb-compare-text {
    display: none;
  }

  .rb-compare-btn--sm .rb-compare-icon {
    margin-right: 0;
  }

  .rb-compare-btn--sm {
    padding: 0 8px;
  }
}

.cmp-body {
  padding: 4px 2px;
}

.cmp-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}

.cmp-title {
  font-size: 13px;
  font-weight: 600;
  color: var(--ui-text-1, #303133);
}

.cmp-modes {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  margin-bottom: 8px;
}

.cmp-mode {
  height: 28px;
  margin-right: 0;
}

.cmp-picker {
  width: 100% !important;
  margin-bottom: 8px;
}

.cmp-period {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  border-radius: 8px;
  background: var(--ui-bg-soft, #f5f7fa);
  color: var(--ui-text-2, #606266);
  font-size: 12px;
  font-weight: 500;
}

.cmp-period-icon {
  font-size: 11px;
  opacity: 0.6;
}

.cmp-hint {
  margin: 8px 0 0;
  font-size: 11px;
  line-height: 1.4;
  color: var(--ui-text-3, #909399);
}
</style>
